import React, { useState } from 'react';
import { 
  Activity, 
  Stethoscope, 
  GraduationCap, 
  Building2, 
  ShieldCheck, 
  CheckCircle2, 
  ArrowRight,
  AlertCircle
} from 'lucide-react';

const specializationsData = [
  {
    id: 'chirurgia',
    title: 'Chirurgia e Area Critica',
    subtitle: 'Chirurgia Generale, Anestesia, Ortopedia, Ginecologia e Chirurgia Specialistica',
    icon: Activity,
    badge: 'Alto Rischio Operativo',
    coverageType: 'RC Colpa Grave e Professionale Diretta',
    maxCoverage: 'Fino a € 5.000.000',
    retroactivity: 'Illimitata / 10 Anni',
    partners: ["AmTrust Medici", "Lloyd's"],
    highlights: [
      'Copertura per colpa grave da rivalsa dell’Azienda Sanitaria / Struttura',
      'Copertura della responsabilità civile diretta per attività libero-professionale',
      'Clausola di retroattività illimitata per sinistri pregressi non noti',
      'Tutela specifica per consensi informati e complicanze intra-operatorie'
    ],
    ctaText: 'Richiedi Analisi Rischio Chirurgico'
  },
  {
    id: 'mmg-pls',
    title: 'Medici di Medicina Generale & PLS',
    subtitle: 'Medici di Famiglia, Pediatri di Libera Scelta, Continuità Assistenziale',
    icon: Stethoscope,
    badge: 'Convenzionati SSN',
    coverageType: 'RC Professionale + Tutela Legale Sanitaria',
    maxCoverage: 'Fino a € 3.000.000',
    retroactivity: '10 Anni di Default (estendibile)',
    partners: ['AmTrust Medici', 'Tutela Legale SPA'],
    highlights: [
      'Piena conformità alla Legge Gelli-Bianco per l’attività convenzionata',
      'Inclusione sostituti di studio e tirocinanti in affiancamento',
      'Difesa penale e civile completa per controversie con assistiti o SSN',
      'Copertura per somministrazione vaccini e diagnostica di primo livello'
    ],
    ctaText: 'Preventivo MMG & PLS'
  },
  {
    id: 'specializzandi',
    title: 'Specializzandi & Neo-Abilitati',
    subtitle: 'Medici in Formazione Specialistica, Guardia Medica, Sostituzioni Under 35',
    icon: GraduationCap,
    badge: 'Tariffa Agevolata Under 35',
    coverageType: 'RC Colpa Grave + Extra-Rete Formativa',
    maxCoverage: 'Fino a € 2.000.000',
    retroactivity: 'Garantita da inizio carriera',
    partners: ["AmTrust Medici", "Lloyd's"],
    highlights: [
      'Premio fortemente agevolato per medici under 35 e specializzandi',
      'Copertura per la sola Colpa Grave in ospedale (obbligatoria)',
      'Estensione automatica per Guardie Mediche e Sostituzioni MMG',
      'Flessibilità d’adeguamento al termine del percorso di specializzazione'
    ],
    ctaText: 'Attiva Copertura Under 35'
  },
  {
    id: 'odontoiatria',
    title: 'Odontoiatria & Libera Professione',
    subtitle: 'Odontoiatri, Stomatologi, Titolari di Studio Dentistico o Poliambulatorio',
    icon: Building2,
    badge: 'Libera Professione & Studio',
    coverageType: 'RC Struttura / Studio + Tutela Legale Penale',
    maxCoverage: 'Personalizzato su Rischio / Fatturato',
    retroactivity: 'Personalizzabile fino a Illimitata',
    partners: ['AmTrust Medici', 'Tutela Legale SPA'],
    highlights: [
      'Protezione per interventi implantologici, protesici e chirurgia orale',
      'Responsabilità Civile verso Terzi (RCT) e Dipendenti/Collaboratori (RCO)',
      'Copertura danni ad apparecchiature e strumentazione di studio',
      'Tutela legale penale e amministrativa per presunti errori terapeutici'
    ],
    ctaText: 'Richiedi Studio Rischio Odontoiatrico'
  }
];

const SpecializationFilter = () => {
  const [activeId, setActiveId] = useState('chirurgia');

  const activeSpec = specializationsData.find((item) => item.id === activeId) || specializationsData[0];

  const handleSelectTab = (id) => {
    setActiveId(id);
  };

  const handleCtaClick = () => {
    const contactSection = document.getElementById('contatti');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-16 bg-white text-slate-900 border-b border-slate-200 relative">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0066FF]/10 text-[#0066FF] text-xs font-bold uppercase tracking-wider mb-4">
            <ShieldCheck size={14} />
            Configuratore Rapido di Rischio
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0B192C] mb-4">
            Seleziona la tua <span className="text-[#0066FF]">Specializzazione Medica</span>
          </h2>
          <p className="text-slate-600 text-base sm:text-lg">
            Soluzioni assicurative ad altissimo profilo istituzionale, costruite in conformità con la Legge Gelli-Bianco e modulate in base all'effettiva operatività professionale.
          </p>
        </div>

        {/* Tab Selection */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8">
          {specializationsData.map((spec) => {
            const Icon = spec.icon;
            const isActive = spec.id === activeId;
            return (
              <button
                key={spec.id}
                onClick={() => handleSelectTab(spec.id)}
                className={`flex items-center gap-3 p-4 rounded-xl text-left transition-all duration-300 border ${
                  isActive
                    ? 'bg-[#0066FF] border-[#0066FF] shadow-lg shadow-[#0066FF]/20 text-white translate-y-[-2px]'
                    : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
                }`}
              >
                <div
                  className={`p-2.5 rounded-lg shrink-0 ${
                    isActive ? 'bg-white/20 text-white' : 'bg-blue-50 text-[#0066FF]'
                  }`}
                >
                  <Icon size={22} />
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm sm:text-base leading-tight truncate">
                    {spec.title}
                  </div>
                  <div className={`text-xs mt-1 truncate ${isActive ? 'text-blue-100' : 'text-slate-500'}`}>
                    {spec.badge}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Active Tab Content Card */}
        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-slate-200 shadow-xl transition-all duration-500">
          <div className="flex flex-col lg:flex-row gap-8 items-start justify-between">
            {/* Main Info */}
            <div className="w-full lg:w-7/12">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <span className="bg-blue-50 border border-[#0066FF]/30 text-[#0066FF] text-xs font-semibold px-3 py-1 rounded-full">
                  {activeSpec.badge}
                </span>
                <span className="text-slate-500 text-xs flex items-center gap-1">
                  <AlertCircle size={13} className="text-[#0066FF]" />
                  Regolamento IVASS & Legge Gelli-Bianco
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-bold text-[#0B192C] mb-2">
                {activeSpec.title}
              </h3>
              <p className="text-slate-600 text-sm sm:text-base mb-6">
                {activeSpec.subtitle}
              </p>

              {/* Grid of Key Features */}
              <div className="space-y-3 mb-6">
                <h4 className="text-xs uppercase font-semibold text-slate-500 tracking-wider">
                  Caratteristiche Chiave del Piano
                </h4>
                {activeSpec.highlights.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3">
                    <CheckCircle2 size={18} className="text-[#0066FF] shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar / Technical Parameters & CTA */}
            <div className="w-full lg:w-5/12 bg-slate-50 p-6 rounded-xl border border-slate-200 flex flex-col justify-between h-full">
              <div>
                <h4 className="text-sm uppercase font-bold text-[#0066FF] tracking-wider mb-4 border-b border-slate-200 pb-2">
                  Parametri di Copertura Consigliati
                </h4>

                <div className="space-y-4 mb-6">
                  <div>
                    <span className="text-xs text-slate-500 block">Tipologia Garanzia</span>
                    <span className="text-sm font-semibold text-[#0B192C]">{activeSpec.coverageType}</span>
                  </div>

                  <div>
                    <span className="text-xs text-slate-500 block">Massimale Consigliato</span>
                    <span className="text-lg font-extrabold text-[#0066FF]">{activeSpec.maxCoverage}</span>
                  </div>

                  <div>
                    <span className="text-xs text-slate-500 block">Retroattività</span>
                    <span className="text-sm font-semibold text-[#0B192C]">{activeSpec.retroactivity}</span>
                  </div>

                  <div>
                    <span className="text-xs text-slate-500 block mb-1">Partner di Riferimento</span>
                    <div className="flex flex-wrap gap-2">
                      {activeSpec.partners.map((partner, pIdx) => (
                        <span
                          key={pIdx}
                          className="bg-white text-slate-800 text-xs px-2.5 py-1 rounded border border-slate-200 font-semibold"
                        >
                          {partner}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCtaClick}
                className="w-full bg-[#0066FF] hover:bg-[#0052cc] text-white font-bold py-3.5 px-6 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg shadow-[#0066FF]/20 hover:scale-[1.02]"
              >
                <span>{activeSpec.ctaText}</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SpecializationFilter;
